'use client'

import { useState, useEffect } from 'react'
import { usePathname } from 'next/navigation'
import AskPixelmojo from './AskPixelmojo'
import {
  getCurrentContext,
  getTriggerRules,
  markAsVisited,
  type ChatContext,
} from '@/lib/chat-context'

/**
 * ChatWidget v3 - Ask Pixelmojo progressive AI search shell
 *
 * Version History:
 * - v3.0 (2026-07-11): Replaced the bottom-right chatbot with the AskPixelmojo
 *   four-state AI search shell (initial / suggestions / answer / minimized).
 *   Data layer extracted to useVectorChat; ChatWindow/ChatGreeting retired.
 * - v2.5 (2026-01-23): JotForm-style greeting panel with quick action buttons
 * - v2.0 (2025-10-10): Lazy session creation, progressive disclosure
 * - v1.0: Scroll-based visibility
 *
 * This file is the global mount + orchestration layer only: pathname
 * exclusions, page context, and time-based visibility. All chat UI lives in
 * AskPixelmojo.tsx; all data logic lives in useVectorChat.ts.
 */
const CHAT_WIDGET_VERSION = '3.0.0'

function ChatWidgetInner() {
  const [isVisible, setIsVisible] = useState(false)
  const [chatContext, setChatContext] = useState<ChatContext | null>(null)

  // Initialize page context on mount (session is created lazily on engagement)
  useEffect(() => {
    if (process.env.NODE_ENV === 'development') {
      console.log(`[ChatWidget v${CHAT_WIDGET_VERSION}] Initialized`)
    }
    setChatContext(getCurrentContext())
    markAsVisited()
  }, [])

  // timeOnPage: no interval. Page entry is timestamped once in
  // chat-context.ts and useVectorChat stamps the elapsed seconds into the
  // payload at message-send time — the only place the value is consumed.
  // (The old 1s setInterval re-rendered the chat shell every second for
  // the life of the tab; removed in the 2026-07-17 perf pass.)

  // Time-based visibility: mobile immediately, high-intent pages after 1s,
  // everything else after 2s (unchanged from v2)
  useEffect(() => {
    const isMobile = typeof window !== 'undefined' && window.innerWidth < 768

    if (isMobile) {
      setIsVisible(true)
      return
    }

    const context = getCurrentContext()
    const triggerRules = getTriggerRules(context.pageType)
    const delay = triggerRules.highIntent ? 1000 : 2000
    const timer = setTimeout(() => setIsVisible(true), delay)
    return () => clearTimeout(timer)
  }, [])

  return <AskPixelmojo chatContext={chatContext} isVisible={isVisible} />
}

export default function ChatWidget() {
  const pathname = usePathname()
  if (pathname?.startsWith('/platform/app')) return null
  return <ChatWidgetInner />
}
